import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { ASSET_LIMITS } from '../config/uploadConfig';

export interface Transition {
  id: string;
  type: string;
  startTime: number;
  duration: number;
  notes?: string;
}

interface TransitionManagerProps {
  transitions: Transition[];
  onTransitionsChange: (transitions: Transition[]) => void;
  videoDuration?: number;
}

/**
 * The stored type stays as configured ("Fade In/Out"), since it is sent to the
 * editor as is; this is only the label: "Fade in/out".
 */
export const transitionLabel = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

const rowGrid = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    sm: 'minmax(0, 1.4fr) 112px 112px minmax(0, 2fr) auto',
  },
  gap: 1.5,
  alignItems: 'center',
} as const;

/** Transitions the creator asks for, each at a time in the video. */
const TransitionManager: React.FC<TransitionManagerProps> = ({
  transitions,
  onTransitionsChange,
  videoDuration = 300 // Default 5 minutes
}) => {
  const [newTransition, setNewTransition] = useState<Partial<Transition>>({
    type: '',
    startTime: 0,
    duration: 1,
    notes: ''
  });

  const handleAddTransition = () => {
    if (!newTransition.type || newTransition.startTime === undefined) return;

    const transition: Transition = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      type: newTransition.type,
      startTime: newTransition.startTime,
      duration: newTransition.duration || 1,
      notes: newTransition.notes || ''
    };

    onTransitionsChange([...transitions, transition]);
    setNewTransition({ type: '', startTime: 0, duration: 1, notes: '' });
  };

  const handleRemoveTransition = (id: string) => {
    onTransitionsChange(transitions.filter(t => t.id !== id));
  };

  const handleUpdateTransition = (id: string, updates: Partial<Transition>) => {
    onTransitionsChange(
      transitions.map(t => t.id === id ? { ...t, ...updates } : t)
    );
  };

  const maxCount = ASSET_LIMITS.transitions.maxCount;
  const canAddMore = transitions.length < maxCount;
  const sortedTransitions = [...transitions].sort((a, b) => a.startTime - b.startTime);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
        <Typography component="h3" sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
          Transitions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
          {transitions.length} of {maxCount}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>
        Ask for a transition at a point in the video. Times are in seconds from the start.
      </Typography>

      {sortedTransitions.length > 0 && (
        <Box component="ul" aria-label="Transitions" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2 }}>
          {sortedTransitions.map((transition) => (
            <Box
              component="li"
              key={transition.id}
              sx={{ ...rowGrid, py: 1.25, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0, pt: 0 } }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
                {transitionLabel(transition.type)}
              </Typography>
              <TextField
                size="small"
                label="Start (s)"
                type="number"
                value={transition.startTime}
                onChange={(e) => handleUpdateTransition(transition.id, {
                  startTime: parseFloat(e.target.value) || 0
                })}
                inputProps={{ min: 0, max: videoDuration, step: 0.1 }}
              />
              <TextField
                size="small"
                label="Duration (s)"
                type="number"
                value={transition.duration}
                onChange={(e) => handleUpdateTransition(transition.id, {
                  duration: parseFloat(e.target.value) || 1
                })}
                inputProps={{
                  min: 0.1,
                  max: ASSET_LIMITS.transitions.maxDuration,
                  step: 0.1
                }}
              />
              <TextField
                size="small"
                label="Notes"
                value={transition.notes || ''}
                onChange={(e) => handleUpdateTransition(transition.id, { notes: e.target.value })}
                placeholder="Anything specific"
                inputProps={{ maxLength: ASSET_LIMITS.notes.maxLength }}
                sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveTransition(transition.id)}
                aria-label={`Remove ${transitionLabel(transition.type).toLowerCase()} transition at ${transition.startTime} seconds`}
                sx={{ justifySelf: { xs: 'start', sm: 'center' } }}
              >
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {canAddMore ? (
        <Box sx={rowGrid}>
          <TextField
            select
            size="small"
            label="Transition"
            value={newTransition.type || ''}
            onChange={(e) => setNewTransition(prev => ({ ...prev, type: e.target.value }))}
            sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
          >
            {ASSET_LIMITS.transitions.types.map((type) => (
              <MenuItem key={type} value={type}>{transitionLabel(type)}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Start (s)"
            type="number"
            value={newTransition.startTime || 0}
            onChange={(e) => setNewTransition(prev => ({
              ...prev,
              startTime: parseFloat(e.target.value) || 0
            }))}
            inputProps={{ min: 0, max: videoDuration, step: 0.1 }}
          />
          <TextField
            size="small"
            label="Duration (s)"
            type="number"
            value={newTransition.duration || 1}
            onChange={(e) => setNewTransition(prev => ({
              ...prev,
              duration: parseFloat(e.target.value) || 1
            }))}
            inputProps={{
              min: 0.1,
              max: ASSET_LIMITS.transitions.maxDuration,
              step: 0.1
            }}
          />
          <TextField
            size="small"
            label="Notes (optional)"
            value={newTransition.notes || ''}
            onChange={(e) => setNewTransition(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="e.g. Slow fade"
            inputProps={{ maxLength: ASSET_LIMITS.notes.maxLength }}
            sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddOutlinedIcon />}
            onClick={handleAddTransition}
            disabled={!newTransition.type || newTransition.startTime === undefined}
            sx={{ justifySelf: 'start', whiteSpace: 'nowrap' }}
          >
            Add
          </Button>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          You've added the maximum of {maxCount} transitions. Remove one to add another.
        </Typography>
      )}
    </Box>
  );
};

export default TransitionManager;
